import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as R from 'ramda';

import { UserProfileResponse } from '@/api/types';
import { RootState } from '@/store';

import HydrateAction from '../hydration';

export type UserState = {
  profile: UserProfileResponse | null;
  isAuthenticated: boolean;
  comscoreFirstPartyIdHash: string | null;
  verifiedPhoneNumber: string | null;
  shouldShowOnboardingNewsletter: boolean;
  shouldShowAppBanner: boolean;
  isLoading: boolean;
  isDataInitialized: boolean;
  showTicketUpgradeOptionsInProfile: boolean;
};

export const initialState: UserState = {
  profile: null,
  isAuthenticated: false,
  isLoading: true,
  isDataInitialized: false,
  comscoreFirstPartyIdHash: null,
  verifiedPhoneNumber: null,
  shouldShowOnboardingNewsletter: false,
  shouldShowAppBanner: true,
  showTicketUpgradeOptionsInProfile: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setSessionData: (
      state,
      action: PayloadAction<{
        profile: UserState['profile'];
        isAuthenticated: UserState['isAuthenticated'];
        comscoreFirstPartyIdHash: UserState['comscoreFirstPartyIdHash'];
        verifiedPhoneNumber: UserState['verifiedPhoneNumber'];
        shouldShowOnboardingNewsletter: UserState['shouldShowOnboardingNewsletter'];
        shouldShowAppBanner: UserState['shouldShowAppBanner'];
      }>,
    ) => {
      // TODO: Refactor
      state.profile = action.payload.profile;
      state.isAuthenticated = action.payload.isAuthenticated;
      state.comscoreFirstPartyIdHash = action.payload.comscoreFirstPartyIdHash;
      state.verifiedPhoneNumber = action.payload.verifiedPhoneNumber;
      state.shouldShowOnboardingNewsletter =
        action.payload.shouldShowOnboardingNewsletter;
      state.shouldShowAppBanner = action.payload.shouldShowAppBanner;
      state.isLoading = false;
      state.isDataInitialized = true;
    },
    setVerifiedPhoneNumber: (state, action: PayloadAction<string | null>) => {
      state.verifiedPhoneNumber = action.payload;
    },
    setShouldShowOnboardingNewsletter: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.shouldShowOnboardingNewsletter = action.payload;
    },
    setShouldShowAppBanner: (state, action: PayloadAction<boolean>) => {
      state.shouldShowAppBanner = action.payload;
    },
    getUserInit: (state) => {
      state.isLoading = true;
    },
    setShowTicketUpgradeOptionsInProfile: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.showTicketUpgradeOptionsInProfile = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(HydrateAction, (state, action) => {
      if (R.equals(state, initialState)) {
        return action.payload.user;
      }

      return state;
    });
  },
});

export const {
  setSessionData,
  getUserInit,
  setVerifiedPhoneNumber,
  setShouldShowOnboardingNewsletter,
  setShouldShowAppBanner,
  setShowTicketUpgradeOptionsInProfile,
} = userSlice.actions;

export const selectUser = (state: RootState) => state.user;

export const selectIsUserLoggedIn = (state: RootState) =>
  state.user.isAuthenticated;

export const selectIsProfileLoaded = (state: RootState) => !!state.user.profile;

export const selectIsUserLoading = (state: RootState) => state.user.isLoading;

export const selectIsUserDataInitialized = (state: RootState) =>
  state.user.isDataInitialized;

export const selectUserProfile = (state: RootState) => state.user.profile;

export const selectVerifiedPhoneNumber = (state: RootState) =>
  state.user.verifiedPhoneNumber;

export const selectShouldShowOnboardingNewsletter = (state: RootState) =>
  state.user.shouldShowOnboardingNewsletter;

export const selectShouldShowAppBanner = (state: RootState) =>
  state.user.shouldShowAppBanner;

export const selectShowTicketUpgradeOptionsInProfile = (state: RootState) =>
  state.user.showTicketUpgradeOptionsInProfile;

export const selectCurrentUserInitials = createSelector(
  selectUserProfile,
  (profile) =>
    `${profile?.firstName.charAt(0) || ''}${profile?.lastName.charAt(0) || ''}`,
);

export default userSlice.reducer;
